import { ERROR_MESSAGES } from "src/app/constants";

export interface Address {
  Line0: string;
  Line1?: string;
  Line2?: string;
  PostalZone: string;
  City: string;
  State: string;
  Country?: string;
}

export function createEmptyAddress(): Address {
  return {
    Line0: '',
    PostalZone: '',
    City: '',
    State: ''
  };
}

export type WithFormattedAddress<T extends {Address: Address}> = T & { FullAddress: string, AddressInTwoLines: string };

export function toWithFormattedAddress<T extends { Address: Address }>(obj: T): WithFormattedAddress<T> {
  return {
    ...obj,
    FullAddress: getFullAddress(obj.Address),
    AddressInTwoLines: getAddressInTwoLines(obj.Address)
  };
}

// --- Mapper -------------------------------------------------------------------------------------

export interface AddressDTO {
  addressLine0: string,
  addressLine1?: string,
  addressLine2?: string,
  postalZone: string,
  cityName: string,
  state: string,
  country?: string
}

export function adaptAddress(item: Partial<AddressDTO>): Address {
  return {
    Line0: item.addressLine0,
    Line1: item.addressLine1 || '',
    Line2: item.addressLine2 || '',
    PostalZone: item.postalZone,
    City: item.cityName,
    State: item.state,
    Country: item.country
  }
}

export function toApiAddress(address: Address): AddressDTO {
  return {
    addressLine0: address.Line0,
    ...(address.Line1 && { addressLine1: address.Line1 }),
    ...(address.Line2 && { addressLine2: address.Line2 }),
    postalZone: address.PostalZone,
    cityName: address.City,
    state: address.State,
    ...(address.Country && { country: address.Country })
  }
}

// --- Transform ----------------------------------------------------------------------------------

export function getFullAddress(address: Address): string {
  return [address.Line0, address.Line1, address.Line2, address.PostalZone, address.City, address.State, address.Country].filter(Boolean).map(s => s.trim().replace(/[\s,]+$/, '')).join(', ');
}

export function getAddressInTwoLines(address: Address, withCountry: boolean = false): string {
  const l1 = [address.Line0, address.Line1, address.Line2].filter(Boolean).map(s => s.trim().replace(/[\s,]+$/, '')).join(', ');
  const l2 = [address.PostalZone, address.City, address.State, withCountry ? address.Country : ''].filter(Boolean).map(s => s.trim().replace(/[\s, ]+$/, '')).join(', ');
  return `${[l1, l2].filter(Boolean).join('\n')}.`;
}

// --- Validator ----------------------------------------------------------------------------------

export function isNullAddress(address: Address): string[] {
  const errors: string[] = [];
  if (address.Line0 == null || address.Line0.trim().length === 0)
    errors.push(ERROR_MESSAGES.EMPTY_FIELD('Address Line 1'));
  if (address.PostalZone == null)
    errors.push(ERROR_MESSAGES.EMPTY_FIELD('Postcode'));
  if (address.City == null || address.City.trim().length === 0)
    errors.push(ERROR_MESSAGES.EMPTY_FIELD('City'));
  if (address.State == null || address.State.trim().length === 0)
    errors.push(ERROR_MESSAGES.EMPTY_FIELD('State'));
  return errors;
}